import React, { useState, useEffect } from 'react';
import { ref, onValue, set, push, remove, get } from 'firebase/database';
import { realtimeDb } from '../firebase';
import { 
  ShoppingBag, 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  Upload, 
  DollarSign,
  Package,
  Palette,
  Target,
  Award,
  Globe,
  Star,
  Eye,
  BarChart3,
  Flag,
  Image as ImageIcon
} from 'lucide-react';
import { toastService } from '../utils/toast';

interface Product {
  id: string;
  name: string;
  description: string;
  category: 'cues' | 'table_themes';
  subcategory: string; // 'basic' | 'purchased' | 'international' | 'pro' for cues, 'flags' | 'colors' | 'patterns' for tables
  price: number;
  strength?: number; // Only for cues
  imageUrl?: string;
  isActive: boolean;
  createdAt: number;
  soldCount: number;
  rating?: number;
  countryCode?: string; // For international items
}

interface ProductCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
}

interface ProductStats {
  totalProducts: number;
  totalSales: number;
  totalRevenue: number;
  topSellingProduct: Product | null;
  categorySales: { [key: string]: number };
}

const ProductsManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState<string>('all');
  const [stats, setStats] = useState<ProductStats>({
    totalProducts: 0,
    totalSales: 0,
    totalRevenue: 0,
    topSellingProduct: null,
    categorySales: {}
  });

  // Form states
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    description: '',
    category: 'cues',
    subcategory: 'basic',
    price: 0,
    strength: 1,
    imageUrl: '',
    isActive: true
  });

  const categories: ProductCategory[] = [
    {
      id: 'cues',
      name: 'Dákók',
      icon: <Target className="h-5 w-5" />,
      description: 'Billiárd dákók különböző kategóriákban'
    },
    {
      id: 'table_themes',
      name: 'Asztal Témák',
      icon: <Palette className="h-5 w-5" />,
      description: 'Asztal testreszabási lehetőségek'
    }
  ];

  const cueSubcategories = [
    { id: 'basic', name: 'Alap', icon: <Package className="h-4 w-4" />, description: 'Ingyenes alapdákók' },
    { id: 'purchased', name: 'Megvásárolt', icon: <ShoppingBag className="h-4 w-4" />, description: 'Aranytallérért vásárolható' },
    { id: 'international', name: 'Nemzetközi', icon: <Globe className="h-4 w-4" />, description: 'Országokat reprezentáló dákók' },
    { id: 'pro', name: 'Profi', icon: <Award className="h-4 w-4" />, description: 'Prémium kategória' }
  ];

  const tableSubcategories = [
    { id: 'flags', name: 'Zászlók', icon: <Flag className="h-4 w-4" />, description: 'Nemzeti zászlók mintái' },
    { id: 'colors', name: 'Színek', icon: <Palette className="h-4 w-4" />, description: 'Színes asztal felületek' },
    { id: 'patterns', name: 'Minták', icon: <ImageIcon className="h-4 w-4" />, description: 'Különféle textúrák és minták' }
  ];

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    calculateStats();
  }, [products]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const productsRef = ref(realtimeDb, 'shop/products');
      onValue(productsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const productsArray = Object.entries(data).map(([id, product]: [string, any]) => ({
            id,
            ...product
          }));
          setProducts(productsArray);
        } else {
          // Mock data for development
          setProducts(generateMockProducts());
        }
        setLoading(false);
      }, (error) => {
        console.error('Error loading products:', error);
        setProducts(generateMockProducts());
        setLoading(false);
      });
    } catch (error) {
      console.error('Error setting up products listener:', error);
      setProducts(generateMockProducts());
      setLoading(false);
    }
  };

  const generateMockProducts = (): Product[] => {
    const mockProducts: Product[] = [
      // Cues
      {
        id: 'cue-1',
        name: 'Kezdő Dákó',
        description: 'Alapértelmezett dákó kezdőknek',
        category: 'cues',
        subcategory: 'basic',
        price: 0,
        strength: 1,
        isActive: true,
        createdAt: Date.now() - 86400000,
        soldCount: 1250,
        rating: 3.5
      },
      {
        id: 'cue-2',
        name: 'Bronz Dákó',
        description: 'Erősebb ütés, jobb kontroll',
        category: 'cues',
        subcategory: 'purchased',
        price: 50,
        strength: 2,
        isActive: true,
        createdAt: Date.now() - 172800000,
        soldCount: 890,
        rating: 4.2
      },
      {
        id: 'cue-3',
        name: 'Magyar Dákó',
        description: 'Magyarország nemzeti színeivel',
        category: 'cues',
        subcategory: 'international',
        price: 100,
        strength: 3,
        countryCode: 'HU',
        isActive: true,
        createdAt: Date.now() - 259200000,
        soldCount: 456,
        rating: 4.7
      },
      {
        id: 'cue-4',
        name: 'Champion Pro',
        description: 'Profi játékosoknak tervezett prémium dákó',
        category: 'cues',
        subcategory: 'pro',
        price: 500,
        strength: 5,
        isActive: true,
        createdAt: Date.now() - 345600000,
        soldCount: 123,
        rating: 4.9
      },
      // Table Themes
      {
        id: 'table-1',
        name: 'Magyar Zászló',
        description: 'Magyarország nemzeti zászlaja az asztalon',
        category: 'table_themes',
        subcategory: 'flags',
        price: 75,
        countryCode: 'HU',
        isActive: true,
        createdAt: Date.now() - 432000000,
        soldCount: 678,
        rating: 4.3
      },
      {
        id: 'table-2',
        name: 'Királykék',
        description: 'Elegáns királykék asztal felület',
        category: 'table_themes',
        subcategory: 'colors',
        price: 25,
        isActive: true,
        createdAt: Date.now() - 518400000,
        soldCount: 1024,
        rating: 4.1
      },
      {
        id: 'table-3',
        name: 'Fa Textúra',
        description: 'Természetes fa mintázat az asztalon',
        category: 'table_themes',
        subcategory: 'patterns',
        price: 150,
        isActive: true,
        createdAt: Date.now() - 604800000,
        soldCount: 234,
        rating: 4.6
      }
    ];
    return mockProducts;
  };

  const calculateStats = () => {
    const totalProducts = products.length;
    const totalSales = products.reduce((sum, product) => sum + product.soldCount, 0);
    const totalRevenue = products.reduce((sum, product) => sum + (product.soldCount * product.price), 0);
    const topSellingProduct = products.reduce((top, product) => 
      product.soldCount > (top?.soldCount || 0) ? product : top, null as Product | null
    );
    
    const categorySales = products.reduce((acc, product) => {
      const key = `${product.category}_${product.subcategory}`;
      acc[key] = (acc[key] || 0) + product.soldCount;
      return acc;
    }, {} as { [key: string]: number });

    setStats({
      totalProducts,
      totalSales,
      totalRevenue,
      topSellingProduct,
      categorySales
    });
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    const matchesSubcategory = subcategoryFilter === 'all' || product.subcategory === subcategoryFilter;
    
    return matchesSearch && matchesCategory && matchesSubcategory;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const loadingToast = toastService.loading('Termék mentése...');

    try {
      if (showEditModal && selectedProduct) {
        // Update existing product
        const productRef = ref(realtimeDb, `shop/products/${selectedProduct.id}`);
        await set(productRef, {
          ...formData,
          id: selectedProduct.id,
          createdAt: selectedProduct.createdAt,
          soldCount: selectedProduct.soldCount
        });
        toastService.dismiss(loadingToast);
        toastService.success('Termék sikeresen frissítve!');
      } else {
        // Add new product
        const productsRef = ref(realtimeDb, 'shop/products');
        const newProductRef = push(productsRef);
        await set(newProductRef, {
          ...formData,
          id: newProductRef.key,
          createdAt: Date.now(),
          soldCount: 0
        });
        toastService.dismiss(loadingToast);
        toastService.success('Termék sikeresen hozzáadva!');
      }

      setShowAddModal(false);
      setShowEditModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving product:', error);
      toastService.dismiss(loadingToast);
      toastService.error('Hiba történt a termék mentése során!');
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setFormData(product);
    setShowEditModal(true);
  };

  const handleDelete = async (productId: string, productName: string) => {
    if (!window.confirm(`Biztosan törölni szeretné a "${productName}" terméket?`)) return;

    const loadingToast = toastService.loading('Termék törlése...');
    try {
      const productRef = ref(realtimeDb, `shop/products/${productId}`);
      await remove(productRef);
      toastService.dismiss(loadingToast);
      toastService.success('Termék sikeresen törölve!');
    } catch (error) {
      console.error('Error deleting product:', error);
      toastService.dismiss(loadingToast);
      toastService.error('Hiba történt a termék törlése során!');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'cues',
      subcategory: 'basic',
      price: 0,
      strength: 1,
      imageUrl: '',
      isActive: true
    });
    setSelectedProduct(null);
  };

  const getSubcategories = () => {
    return formData.category === 'cues' ? cueSubcategories : tableSubcategories;
  };

  const getProductIcon = (product: Product) => {
    if (product.category === 'cues') {
      switch (product.subcategory) {
        case 'basic': return <Package className="h-5 w-5 text-gray-500" />;
        case 'purchased': return <ShoppingBag className="h-5 w-5 text-blue-500" />;
        case 'international': return <Globe className="h-5 w-5 text-green-500" />;
        case 'pro': return <Award className="h-5 w-5 text-yellow-500" />;
        default: return <Target className="h-5 w-5" />;
      }
    } else {
      switch (product.subcategory) {
        case 'flags': return <Flag className="h-5 w-5 text-red-500" />;
        case 'colors': return <Palette className="h-5 w-5 text-purple-500" />;
        case 'patterns': return <ImageIcon className="h-5 w-5 text-indigo-500" />;
        default: return <Palette className="h-5 w-5" />;
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Termékek Kezelése</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Shop termékek, dákók és asztal témák adminisztrációja
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Új Termék</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Összes Termék
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.totalProducts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Összes Eladás
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.totalSales.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DollarSign className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Bevétel
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {stats.totalRevenue.toLocaleString()} 🪙
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Star className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Top Termék
                  </dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {stats.topSellingProduct?.name || 'N/A'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="form-label">Keresés</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                  placeholder="Termék neve vagy leírása..."
                />
              </div>
            </div>

            <div>
              <label className="form-label">Kategória</label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setSubcategoryFilter('all');
                }}
                className="form-input"
              >
                <option value="all">Összes kategória</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Alkategória</label>
              <select
                value={subcategoryFilter}
                onChange={(e) => setSubcategoryFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">Összes alkategória</option>
                {categoryFilter === 'cues' && cueSubcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
                {categoryFilter === 'table_themes' && tableSubcategories.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Termékek ({filteredProducts.length})
          </h3>
        </div>
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Termék
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kategória
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ár
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Eladások
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Értékelés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Állapot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getProductIcon(product)}
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {categories.find(c => c.id === product.category)?.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {product.category === 'cues' 
                          ? cueSubcategories.find(s => s.id === product.subcategory)?.name
                          : tableSubcategories.find(s => s.id === product.subcategory)?.name
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {product.price === 0 ? 'Ingyenes' : `${product.price} 🪙`}
                      </div>
                      {product.strength && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Erősség: {product.strength}/5
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {product.soldCount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {product.rating && (
                        <div className="flex items-center">
                          <Star className="h-4 w-4 text-yellow-400 fill-current" />
                          <span className="ml-1 text-sm text-gray-900 dark:text-white">
                            {product.rating.toFixed(1)}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.isActive 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {product.isActive ? 'Aktív' : 'Inaktív'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id, product.name)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              resetForm();
            }}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                    {showEditModal ? 'Termék Szerkesztése' : 'Új Termék Hozzáadása'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Termék neve</label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="form-input"
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">Leírás</label>
                      <textarea
                        value={formData.description || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        className="form-input"
                        rows={3}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Kategória</label>
                        <select
                          value={formData.category || 'cues'}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            category: e.target.value as 'cues' | 'table_themes',
                            subcategory: e.target.value === 'cues' ? 'basic' : 'flags'
                          }))}
                          className="form-input"
                        >
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="form-label">Alkategória</label>
                        <select
                          value={formData.subcategory || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                          className="form-input"
                        >
                          {getSubcategories().map(sub => (
                            <option key={sub.id} value={sub.id}>
                              {sub.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Ár (aranytallér)</label>
                        <input
                          type="number"
                          min="0"
                          value={formData.price || 0}
                          onChange={(e) => setFormData(prev => ({ ...prev, price: parseInt(e.target.value) }))}
                          className="form-input"
                          required
                        />
                      </div>

                      {formData.category === 'cues' && (
                        <div>
                          <label className="form-label">Erősség (1-5)</label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={formData.strength || 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, strength: parseInt(e.target.value) }))}
                            className="form-input"
                          />
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="form-label">Kép URL (opcionális)</label>
                      <input
                        type="url"
                        value={formData.imageUrl || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                        className="form-input"
                        placeholder="https://example.com/image.jpg"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive || false}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 text-sm text-gray-900 dark:text-white">
                        Termék aktív
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="btn-primary w-full sm:w-auto sm:ml-3"
                  >
                    {showEditModal ? 'Frissítés' : 'Hozzáadás'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setShowEditModal(false);
                      resetForm();
                    }}
                    className="btn-secondary w-full sm:w-auto mt-3 sm:mt-0"
                  >
                    Mégse
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsManagement; 